import { Capacitor, registerPlugin } from '@capacitor/core';

const DirectGalleryPlugin = registerPlugin('DirectGallery');

function requireSessionId(sessionId) {
  const value = String(sessionId || '').trim();
  if (!value) throw new TypeError('Direct Gallery session ID is required');
  return value;
}

export function createDirectGalleryController({
  isNative = Capacitor.isNativePlatform(),
  plugin = DirectGalleryPlugin
} = {}) {
  const supportedPlugin = isNative === true && plugin;

  return {
    async getCapability() {
      if (!supportedPlugin) return { supported: false, reason: 'web' };
      return plugin.getCapability();
    },

    async createSession(metadata) {
      if (!supportedPlugin) throw new Error('Direct Gallery is unsupported');
      return plugin.createSession(metadata || {});
    },

    async listPending() {
      if (!supportedPlugin) return [];
      const result = await plugin.listPendingSessions();
      return Array.isArray(result?.sessions) ? result.sessions : [];
    },

    async finalize(sessionId) {
      if (!supportedPlugin) throw new Error('Direct Gallery is unsupported');
      return plugin.finalizeSession({ sessionId: requireSessionId(sessionId) });
    },

    async publishPartial(sessionId) {
      if (!supportedPlugin) throw new Error('Direct Gallery is unsupported');
      return plugin.publishPartial({ sessionId: requireSessionId(sessionId) });
    },

    async abort(sessionId, retainPartial = true) {
      if (!supportedPlugin) throw new Error('Direct Gallery is unsupported');
      return plugin.abortSession({
        sessionId: requireSessionId(sessionId),
        retainPartial: retainPartial !== false
      });
    },

    async deletePending(sessionId, confirmed) {
      if (!supportedPlugin) throw new Error('Direct Gallery is unsupported');
      if (confirmed !== true) {
        throw new Error('Direct Gallery deletion requires confirmation');
      }
      return plugin.deletePendingSession({
        sessionId: requireSessionId(sessionId),
        confirmed: true
      });
    }
  };
}

export const directGalleryController = createDirectGalleryController();

export const getDirectGalleryCapability = () =>
  directGalleryController.getCapability();
export const createDirectGallerySession = metadata =>
  directGalleryController.createSession(metadata);
export const listPendingGallerySessions = () =>
  directGalleryController.listPending();
export const finalizeDirectGallerySession = sessionId =>
  directGalleryController.finalize(sessionId);
export const publishPartialGallerySession = sessionId =>
  directGalleryController.publishPartial(sessionId);
export const deletePendingGallerySession = (sessionId, confirmed) =>
  directGalleryController.deletePending(sessionId, confirmed);
